import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'

export type SearchHighlightMatch = {
  id: number
  bboxes: NormalizedBBox[]
  isRightPage?: boolean
}

export type SearchHighlightData = {
  matches: SearchHighlightMatch[]
  activeMatchId: number | null
}

type SearchHighlightOverlayProps = {
  highlights: SearchHighlightData
  imageNatural: { w: number; h: number }
  firstPageWidth?: number
  isMultipage?: boolean
}

const INACTIVE_FILL = 'rgba(255, 165, 0, 0.25)'
const ACTIVE_FILL = 'rgba(255, 165, 0, 0.45)'
const PADDING = 2

function getPageLayout(
  match: SearchHighlightMatch,
  imageNatural: { w: number; h: number },
  firstPageWidth: number | undefined,
  isMultipage: boolean | undefined
): { pageWidth: number; xOffset: number } {
  if (!isMultipage || !firstPageWidth) {
    return { pageWidth: imageNatural.w, xOffset: 0 }
  }
  if (match.isRightPage) {
    return {
      pageWidth: imageNatural.w - firstPageWidth,
      xOffset: firstPageWidth,
    }
  }
  return { pageWidth: firstPageWidth, xOffset: 0 }
}

function HighlightRect({
  bbox,
  pageWidth,
  xOffset,
  imageHeight,
  isActive,
}: {
  bbox: NormalizedBBox
  pageWidth: number
  xOffset: number
  imageHeight: number
  isActive: boolean
}) {
  return (
    <rect
      x={xOffset + bbox.left * pageWidth - PADDING}
      y={bbox.top * imageHeight - PADDING}
      width={bbox.width * pageWidth + PADDING * 2}
      height={bbox.height * imageHeight + PADDING * 2}
      rx={2}
      ry={2}
      fill={isActive ? ACTIVE_FILL : INACTIVE_FILL}
      stroke='none'
    />
  )
}

export function SearchHighlightOverlay({
  highlights,
  imageNatural,
  firstPageWidth,
  isMultipage,
}: SearchHighlightOverlayProps) {
  if (highlights.matches.length === 0) return null

  return (
    <g style={{ pointerEvents: 'none' }}>
      {highlights.matches.flatMap((match) => {
        const isActive = match.id === highlights.activeMatchId
        const { pageWidth, xOffset } = getPageLayout(
          match,
          imageNatural,
          firstPageWidth,
          isMultipage
        )

        return match.bboxes.map((bbox, bboxIdx) => (
          <HighlightRect
            key={`${match.id}-${bboxIdx}`}
            bbox={bbox}
            pageWidth={pageWidth}
            xOffset={xOffset}
            imageHeight={imageNatural.h}
            isActive={isActive}
          />
        ))
      })}
    </g>
  )
}
